import { PackageLimits } from '../../userPackage';
import { PackageType } from '../../../../types';

export type FeatureKey =
  | 'librarySize'
  | 'transcription'
  | 'askAI'
  | 'download'
  | 'priorityTranscription'
  | 'AINotes'
  | 'AISummaries'
  | 'integrations'
  | 'prioritySupport'
  | 'trainingSession'
  | 'customVocabulary'
  | 'fullTranscript'
  | 'AskAITimestamps'
  | 'advancedPlayback'
  | 'upload'
  | 'bulkUpload'
  | 'translateTranscript'
  | 'generalUpgrade'
  | 'skipQueue'
  | 'copyTranscript'
  | 'folderManagement'
  | 'login'
  | 'templates'
  | 'uploadButton'
  | 'importModalUploadButton'
  | 'unlimitedCredits'
  | 'customVocab'
  | 'importUrlButton'
  | 'multipleRecordingAskAI'
  | 'timeLimit'
  | 'screenRecorder'
  | 'audioRecorder'
  | 'downloadTranscript'
  | 'fullChapterAccess'
  | 'googleMeetBot';

interface Feature {
  title: string;
  usageKey?: keyof PackageLimits;
  description: string;
  ranking: number;
  annualtitle?: string;
  annualDescription?: string;
}

export type FeatureMap = Partial<
  {
    [key in FeatureKey]: Feature;
  }
>;

const freePlanFeatureMap: FeatureMap = {
  timeLimit: {
    title: '45 Minute Time Limit',
    description: 'Record or upload files up to 45 minutes in length.',
    ranking: 2,
  },
  transcription: {
    title: '1 Transcription per month',
    usageKey: 'transcriptionCount',
    description: 'Transcribe one recording.',
    ranking: 3,
  },
  AISummaries: {
    title: 'AI Summaries',
    description: 'Get AI-generated summaries of your recordings.',
    ranking: 4,
  },
  AINotes: {
    title: 'Detailed AI Notes',
    description: 'Get detailed AI-generated notes from your recordings.',
    ranking: 5,
  },
  screenRecorder: {
    title: 'Screen Recorder',
    description: 'Record your screen with audio.',
    ranking: 6,
  },
  audioRecorder: {
    title: 'One Click Audio Recorder',
    description: 'Record audio with a single click.',
    ranking: 7,
  },
  askAI: {
    title: '3 Ask AI Credits',
    usageKey: 'askAICount',
    description:
      'Get insights using 3 AI prompts. Credits can be used for Ask AI questions and template generation.',
    ranking: 8,
  },
};

export const growthFeatureMap: FeatureMap = {
  librarySize: {
    title: '30 Recordings per Month',
    usageKey: 'librarySize',
    description:
      'Save up to 30 audio or video files in your library per month. Limits reset every month.',
    ranking: 1,
    annualtitle: '360 Recordings per Year',
    annualDescription:
      'Store up to 360 audio or video files in your library per year.',
  },
  transcription: {
    title: '30 Transcriptions per Month',
    usageKey: 'transcriptionCount',
    description:
      'Transcribe up to 30 recordings per month. Limits reset every month.',
    ranking: 2,
    annualtitle: '360 Transcriptions per Year',
    annualDescription:
      'Transcribe 360 files a year. These can be done at any time during the year.',
  },
  askAI: {
    title: '30 AI Credits a Month',
    usageKey: 'askAICount',
    description:
      'Use credits for Ask AI questions and template generation. Summarize, take notes, and extract insights from your recordings. Limits reset every month.',
    ranking: 3,
    annualtitle: '360 Ask AI Credits a Year',
    annualDescription:
      'Use credits for Ask AI questions and template generation. Summarize, take notes, and extract insights from your recordings. These Ask AI credits can be used any time during the year.',
  },
  skipQueue: {
    title: 'Skip the Wait',
    description:
      'Prioritize your transcripts and summaries for faster processing.',
    ranking: 4,
  },
  AISummaries: {
    title: 'AI Summaries',
    usageKey: 'transcriptionCount',
    description: 'Get comprehensive, timestamped summaries and notes.',
    ranking: 5,
  },
  AINotes: {
    title: 'Detailed AI Notes',
    usageKey: 'transcriptionCount',
    description: 'Get detailed, timestamped notes from your recordings.',
    ranking: 6,
  },
  fullTranscript: {
    title: 'Full Transcripts',
    description: 'Get full transcripts with automatic speaker identification.',
    ranking: 7,
  },
  templates: {
    title: 'Templates',
    description: 'Create and use custom templates for your recordings.',
    ranking: 8,
  },
  prioritySupport: {
    title: 'Priority Support',
    usageKey: 'priority',
    description: 'Dedicated support from our Customer Success Manager.',
    ranking: 9,
  },
  trainingSession: {
    title: 'Training Session',
    usageKey: 'priority',
    description: 'Personalized training session with our team.',
    ranking: 10,
  },
  download: {
    title: 'Downloads',
    usageKey: 'mp4DownloadCount',
    description: 'Download recordings to your computer.',
    ranking: 11,
  },
  advancedPlayback: {
    title: 'Advanced Playback',
    description: 'Control playback speed and access other advanced features.',
    ranking: 12,
  },
  translateTranscript: {
    title: 'Translate Transcripts',
    description: 'Translate your transcripts to over 50 languages.',
    ranking: 13,
  },
  generalUpgrade: {
    title: 'Do More with a Plan',
    usageKey: 'generalUpgrade',
    description:
      'Unlock all premium features. Advanced transcriptions, Templates, Summaries, AI notes, and Ask AI.',
    ranking: 14,
  },
  priorityTranscription: {
    title: 'Priority Transcriptions',
    usageKey: 'priority',
    description: 'Faster transcription speeds with priority processing.',
    ranking: 15,
  },
  copyTranscript: {
    title: 'Copy and Export Full Transcript',
    description: 'Copy and export full timestamped transcripts with speakers.',
    ranking: 16,
  },
  upload: {
    title: 'Upload Recordings',
    usageKey: 'uploadCount',
    description: 'Upload audio or video files to your library.',
    ranking: 17,
  },
  folderManagement: {
    title: 'Folder Management',
    description: 'Create and organize folders for your recordings.',
    ranking: 18,
  },
  login: {
    title: 'Do More with a Plan',
    description:
      'Unlock all premium features. Advanced transcriptions, Templates, Summaries, AI notes, and Ask AI.',
    ranking: 19,
  },
  AskAITimestamps: {
    title: 'Ask AI Timestamps',
    description: 'Access timestamps in Ask AI.',
    ranking: 20,
  },
  uploadButton: {
    title: 'Upload Files',
    description:
      'Upload audio or video files (MP4, MP3, WebM, etc.) to your library.',
    ranking: 21,
  },
  importUrlButton: {
    title: 'Import from URL',
    description:
      'Import and transcribe from YouTube, Vimeo, Twitter and other URLs.',
    ranking: 22,
  },
  multipleRecordingAskAI: {
    title: 'Multiple Recording Ask AI',
    description: 'Search or create content from multiple recordings at once.',
    ranking: 23,
  },
  downloadTranscript: {
    title: 'Download Transcripts',
    description:
      'Download transcripts in various formats (TXT, DOCX, PDF, RTF)',
    ranking: 29,
  },
  importModalUploadButton: {
    title: 'Upload Files in Import Modal',
    description: 'Upload audio or video files through the import modal.',
    ranking: 30,
  },
  fullChapterAccess: {
    title: 'Full Chapter Access',
    description: 'Access all chapters and summaries in your recordings.',
    ranking: 31,
  },
};

const businessFeatureMap: FeatureMap = {
  librarySize: {
    title: 'Unlimited Recordings',
    usageKey: 'librarySize',
    description: 'Save unlimited audio or video files in your library.',
    ranking: 1,
  },
  transcription: {
    title: 'Unlimited Transcriptions',
    usageKey: 'transcriptionCount',
    description: 'Transcribe unlimited recordings.',
    ranking: 2,
  },
  askAI: {
    title: 'Unlimited AI Credits',
    usageKey: 'askAICount',
    description:
      'Use unlimited credits for Ask AI questions and template generation. Summarize, take notes, and extract insights from your recordings without any limits.',
    ranking: 3,
  },
  skipQueue: {
    title: 'Skip the Wait',
    description:
      'Prioritize your transcripts and summaries for faster processing.',
    ranking: 4,
  },
  AISummaries: {
    title: 'AI Summaries',
    usageKey: 'transcriptionCount',
    description: 'Get comprehensive, timestamped summaries and notes.',
    ranking: 5,
  },
  AINotes: {
    title: 'Detailed AI Notes',
    usageKey: 'transcriptionCount',
    description: 'Get detailed, timestamped notes from your recordings.',
    ranking: 6,
  },
  fullTranscript: {
    title: 'Full Transcripts',
    description: 'Get full transcripts with automatic speaker identification.',
    ranking: 7,
  },
  templates: {
    title: 'Templates',
    description: 'Create and use custom templates for your recordings.',
    ranking: 8,
  },
  prioritySupport: {
    title: 'Priority Support',
    description: 'Dedicated support from our Customer Success Manager.',
    ranking: 9,
  },
  trainingSession: {
    title: 'Training Session',
    description: 'Personalized training session with our team.',
    ranking: 10,
  },
  download: {
    title: 'Downloads',
    usageKey: 'mp4DownloadCount',
    description: 'Download recordings to your computer.',
    ranking: 11,
  },
  advancedPlayback: {
    title: 'Advanced Playback',
    description: 'Control playback speed and access other advanced features.',
    ranking: 12,
  },
  integrations: {
    title: 'Integrations',
    description:
      'Automate workflows with integrations via Zapier, API, or Webhooks.',
    ranking: 13,
  },
  customVocabulary: {
    title: 'Custom Vocabulary',
    description: 'Improve transcription accuracy with custom vocabulary.',
    ranking: 14,
  },
  bulkUpload: {
    title: 'Bulk Upload',
    description: 'Upload multiple files at once.',
    ranking: 15,
  },
  translateTranscript: {
    title: 'Translate Transcripts',
    description: 'Translate your transcripts to over 50 languages.',
    ranking: 16,
  },
  generalUpgrade: {
    title: 'Do More with a Plan',
    description:
      'Unlock all premium features. Advanced transcriptions, Templates, Summaries, AI notes, and Ask AI.',
    ranking: 17,
  },
  priorityTranscription: {
    title: 'Priority Transcriptions',
    description: 'Faster transcription speeds with priority processing.',
    ranking: 18,
  },
  copyTranscript: {
    title: 'Copy and Export Full Transcript',
    description: 'Copy and export full timestamped transcripts with speakers.',
    ranking: 19,
  },
  upload: {
    title: 'Upload Recordings',
    usageKey: 'uploadCount',
    description: 'Upload audio or video files to your library.',
    ranking: 20,
  },
  folderManagement: {
    title: 'Folder Management',
    description: 'Create and organize folders for your recordings.',
    ranking: 21,
  },
  login: {
    title: 'Do More with a Plan',
    description:
      'Unlock all premium features. Advanced transcriptions, summaries, AI notes, and ask AI.',
    ranking: 22,
  },
  AskAITimestamps: {
    title: 'Ask AI Timestamps',
    description: 'Access timestamps in Ask AI.',
    ranking: 23,
  },
  customVocab: {
    title: 'Custom Vocabulary',
    description: 'Add custom vocabulary to improve transcription accuracy.',
    ranking: 24,
  },
  uploadButton: {
    title: 'Upload Files',
    description:
      'Upload audio or video files (MP4, MP3, WebM, etc.) to your library.',
    ranking: 25,
  },
  unlimitedCredits: {
    title: 'Unlimited AI Credits',
    description:
      'Get unlimited AI credits for Ask AI questions and template generation.',
    ranking: 26,
  },
  importUrlButton: {
    title: 'Import from URL',
    description:
      'Import and transcribe from YouTube, Vimeo, Twitter and other URLs.',
    ranking: 27,
  },
  multipleRecordingAskAI: {
    title: 'Multiple Recording Ask AI',
    description: 'Search or create content from multiple recordings at once.',
    ranking: 28,
  },
  downloadTranscript: {
    title: 'Download Transcripts',
    description:
      'Download transcripts in various formats (TXT, DOCX, PDF, RTF)',
    ranking: 29,
  },
  importModalUploadButton: {
    title: 'Upload Files in Import Modal',
    description: 'Upload audio or video files through the import modal.',
    ranking: 30,
  },
  fullChapterAccess: {
    title: 'Full Chapter Access',
    description:
      'Unlimited access to all chapters and summaries in your recordings.',
    ranking: 31,
  },
  googleMeetBot: {
    title: 'Google Meet Bot',
    description: 'Automatically join and record your Google Meet calls',
    ranking: 32,
  },
};

const standardFeaturesMap = growthFeatureMap;

const premiumFeaturesMap = growthFeatureMap;

const planFeatures: Partial<
  {
    [key in PackageType]: FeatureMap;
  }
> = {
  FREE_LOGGEDIN: freePlanFeatureMap,
  STANDARD: standardFeaturesMap,
  PREMIUM: premiumFeaturesMap,
  BUSINESS: businessFeatureMap,
  GROWTH: growthFeatureMap,
};

export default planFeatures;
